import { useState, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { clinicHoursService } from '../services/clinicHoursService'
import { ClinicHours, DayHours, DAYS_OF_WEEK } from '../types/clinicHours'
import './SettingsClinicHours.css'

export function SettingsClinicHours() {
  const queryClient = useQueryClient()
  const [formData, setFormData] = useState<ClinicHours | null>(null)

  const { data: clinicHours, isLoading, error } = useQuery({
    queryKey: ['clinic-hours'],
    queryFn: () => {
      console.log('[SettingsClinicHours] Fetching clinic hours...')
      return clinicHoursService.getCurrent()
    },
  })

  // Handle errors separately
  useEffect(() => {
    if (error) {
      console.error('[SettingsClinicHours] Error loading clinic hours:', error)
    }
  }, [error])

  // Update formData when clinicHours changes
  useEffect(() => {
    if (clinicHours) {
      console.log('[SettingsClinicHours] Setting formData from clinicHours:', clinicHours)
      setFormData(clinicHours)
    }
  }, [clinicHours])

  const updateMutation = useMutation({
    mutationFn: (data: Partial<ClinicHours>) => clinicHoursService.update(data as any),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['clinic-hours'] })
      queryClient.invalidateQueries({ queryKey: ['available-slots'] })
      alert('Clinic hours updated successfully')
    },
  })

  const handleDayChange = (dayOfWeek: number, field: keyof DayHours, value: any) => {
    if (!formData) return

    const updatedDays = formData.days.map((day) => {
      if (day.dayOfWeek === dayOfWeek) {
        return { ...day, [field]: value }
      }
      return day
    })

    setFormData({ ...formData, days: updatedDays })
  }

  const handleAddClosedPeriod = (dayOfWeek: number) => {
    if (!formData) return

    const updatedDays = formData.days.map((day) => {
      if (day.dayOfWeek === dayOfWeek) {
        const closedPeriods = day.closedPeriods || []
        return {
          ...day,
          closedPeriods: [
            ...closedPeriods,
            { startTime: '12:00', endTime: '13:00' }, // Default lunch break
          ],
        }
      }
      return day
    })

    setFormData({ ...formData, days: updatedDays })
  }

  const handleRemoveClosedPeriod = (dayOfWeek: number, index: number) => {
    if (!formData) return

    const updatedDays = formData.days.map((day) => {
      if (day.dayOfWeek === dayOfWeek) {
        const closedPeriods = day.closedPeriods || []
        return {
          ...day,
          closedPeriods: closedPeriods.filter((_, i) => i !== index),
        }
      }
      return day
    })

    setFormData({ ...formData, days: updatedDays })
  }

  const handleClosedPeriodChange = (
    dayOfWeek: number,
    index: number,
    field: 'startTime' | 'endTime',
    value: string
  ) => {
    if (!formData) return

    const updatedDays = formData.days.map((day) => {
      if (day.dayOfWeek === dayOfWeek) {
        const closedPeriods = day.closedPeriods || []
        const updatedPeriods = closedPeriods.map((period, i) => {
          if (i === index) {
            return { ...period, [field]: value }
          }
          return period
        })
        return { ...day, closedPeriods: updatedPeriods }
      }
      return day
    })

    setFormData({ ...formData, days: updatedDays })
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!formData) return

    try {
      await updateMutation.mutateAsync(formData)
    } catch (error: any) {
      alert(error?.message || 'Failed to update clinic hours')
    }
  }

  const handleReset = () => {
    if (clinicHours) {
      setFormData({ ...clinicHours })
    } else {
      setFormData(null)
    }
  }

  if (isLoading) {
    return <div className="loading">Loading clinic hours...</div>
  }

  if (error) {
    console.error('[SettingsClinicHours] Error:', error)
    return <div className="error">Failed to load clinic hours: {error instanceof Error ? error.message : 'Unknown error'}</div>
  }

  // Use clinicHours directly if formData hasn't been set yet (useEffect might not have run)
  const displayData = formData || clinicHours

  if (!displayData) {
    return <div className="error">Failed to load clinic hours (no data available)</div>
  }

  return (
    <div className="settings-clinic-hours">
      <div className="settings-section-header">
        <h2>Clinic Hours</h2>
        <p>Configure operating hours and available appointment slots for each day of the week.</p>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="clinic-hours-form">
          <div className="form-group">
            <label htmlFor="name">Name</label>
            <input
              id="name"
              type="text"
              value={displayData.name}
              onChange={(e) => setFormData(displayData ? { ...displayData, name: e.target.value } : null)}
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="description">Description</label>
            <textarea
              id="description"
              value={displayData.description || ''}
              onChange={(e) => setFormData(displayData ? { ...displayData, description: e.target.value } : null)}
              rows={3}
            />
          </div>

          <div className="clinic-hours-days">
            <h3>Days of the Week</h3>
            <div className="days-grid">
              {DAYS_OF_WEEK.map((day) => {
                const dayHours = displayData.days.find((d) => d.dayOfWeek === day.value) || {
                  dayOfWeek: day.value,
                  isOpen: false,
                  startTime: '09:00',
                  endTime: '17:00',
                  slotDuration: 30,
                }

                return (
                  <div key={day.value} className="day-config">
                    <div className="day-header">
                      <label className="day-checkbox">
                        <input
                          type="checkbox"
                          checked={dayHours.isOpen}
                          onChange={(e) => handleDayChange(day.value, 'isOpen', e.target.checked)}
                        />
                        <span className="day-name">{day.label}</span>
                      </label>
                    </div>

                    {dayHours.isOpen && (
                      <div className="day-fields">
                        <div className="form-row">
                          <div className="form-group">
                            <label>Start Time</label>
                            <input
                              type="time"
                              value={dayHours.startTime}
                              onChange={(e) => handleDayChange(day.value, 'startTime', e.target.value)}
                              required={dayHours.isOpen}
                            />
                          </div>
                          <div className="form-group">
                            <label>End Time</label>
                            <input
                              type="time"
                              value={dayHours.endTime}
                              onChange={(e) => handleDayChange(day.value, 'endTime', e.target.value)}
                              required={dayHours.isOpen}
                            />
                          </div>
                        </div>
                        <div className="form-group">
                          <label>Slot Duration (minutes)</label>
                          <select
                            value={dayHours.slotDuration}
                            onChange={(e) => handleDayChange(day.value, 'slotDuration', Number(e.target.value))}
                          >
                            <option value="15">15 minutes</option>
                            <option value="30">30 minutes</option>
                            <option value="45">45 minutes</option>
                            <option value="60">60 minutes</option>
                          </select>
                        </div>

                        <div className="closed-periods-section">
                          <div className="closed-periods-header">
                            <label>Closed Periods (e.g., lunch breaks)</label>
                            <button
                              type="button"
                              className="btn-add-closed-period"
                              onClick={() => handleAddClosedPeriod(day.value)}
                            >
                              + Add Closed Period
                            </button>
                          </div>
                          {dayHours.closedPeriods && dayHours.closedPeriods.length > 0 && (
                            <div className="closed-periods-list">
                              {dayHours.closedPeriods.map((period, index) => (
                                <div key={index} className="closed-period-item">
                                  <div className="form-row">
                                    <div className="form-group">
                                      <label>From</label>
                                      <input
                                        type="time"
                                        value={period.startTime}
                                        onChange={(e) =>
                                          handleClosedPeriodChange(day.value, index, 'startTime', e.target.value)
                                        }
                                      />
                                    </div>
                                    <div className="form-group">
                                      <label>To</label>
                                      <input
                                        type="time"
                                        value={period.endTime}
                                        onChange={(e) =>
                                          handleClosedPeriodChange(day.value, index, 'endTime', e.target.value)
                                        }
                                      />
                                    </div>
                                    <div className="form-group form-group-button">
                                      <button
                                        type="button"
                                        className="btn-remove-closed-period"
                                        onClick={() => handleRemoveClosedPeriod(day.value, index)}
                                        title="Remove closed period"
                                      >
                                        ×
                                      </button>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          </div>

          <div className="form-actions">
            <button type="button" className="btn-secondary" onClick={handleReset}>
              Reset
            </button>
            <button type="submit" className="btn-primary" disabled={updateMutation.isPending}>
              {updateMutation.isPending ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </div>
      </form>
    </div>
  )
}

